/**
 * @module
 * @public
 * @name CompanyDirectoryContact
 * @description
 * This class is used to represent an entry in the collective directory of a company
 */
export class DirectoryContact {

    /**
     * @public
     * @property {string} id - Directory entry identifier
     *
     */
    public id: string;

    /**
     * @public
     * @name directoryType
     * @enum {string} Defines the type of directory contact: "company" or "personal"
     * @readonly
     */
    public directoryType: string;

    /**
     * @public
     * @property {string} companyId - Id of the company (only for entries from "company" directory type)
     *
     */
    public companyId: string;

    /**
     * @public
     * @property {string} userId - Id of the user owner of the contact (only for entries from "personal" directory type)
     *
     */
    public userId: string;

    /**
     * @public
     * @property {string} firstName - First name of the contact
     *
     */
    public firstName: string;

    /**
     * @public
     * @property {string} lastName - Last name of the contact
     *
     */
    public lastName: string;

    /**
     * @public
     * @property {string} eMail - Email address of the contact
     *
     */
    public eMail: string;

    /**
     * @public
     * @property {string} jobTitle - Job title
     *
     */
    public jobTitle: string;

    /**
     * @public
     * @property {string} department - Department
     *
     */
    public department: string;

    /**
     * @public
     * @property {string} companyName - Name of the company
     *
     */
    public companyName: string;

    /**
     * @public
     * @property {string} street - Contact address : Street
     *
     */
    public street: string;

    /**
     * @public
     * @property {string} city - Contact address : City
     *
     */
    public city: string;

    /**
     * @public
     * @property {string} state - Contact address : State (only when country is 'USA' or 'CAN')
     *
     */
    public state: string;

    /**
     * @public
     * @property {string} postalCode - Contact address : Postal code / ZIP
     *
     */
    public postalCode: string;

    /**
     * @public
     * @property {string} country - Contact address : Country
     *
     */
    public country: string;

    /**
     * @public
     * @property {Array<string>} workPhoneNumbers - Work phone numbers (E164 format! e.g: ["+33390671234"])
     *
     */
    public workPhoneNumbers: string[];

    /**
     * @public
     * @property {Array<string>} mobilePhoneNumbers - Mobile phone numbers (E164 format! e.g: ["+33690671234", "+33 6 90 67 67 91"])
     *
     */
    public mobilePhoneNumbers: string[];

    /**
     * @public
     * @property {Array<string>} otherPhoneNumbers - Other phone numbers (E164 format! e.g: ["+33690671234", "+33 6 90 67 67 91"])
     *
     */
    public otherPhoneNumbers: string[];

    /**
     * @public
     * @property {string} custom1 - Custom field
     *
     */
    public custom1: string;

    /**
     * @public
     * @property {string} custom2 - Other custom field
     *
     */
    public custom2: string;

    /**
     * @public
     * @property {string} tags - contact tags
     *
     */
    public tags: string[];

    /**
     * @public
     * @property {object[]} aliases - Aliases of the directory entry
     *
     */
    public aliases: { type: string; code: string }[];


    /**
     * @public
     * @param {object} data The object containing the attributes of the company directory contact
     * @returns {DirectoryContact} the created company directory contact
     */
    public static createFromData(data: any): DirectoryContact {
        return new DirectoryContact(
            data.directoryType,
            data.companyId,
            data.userId,
            data.id,
            data.firstName,
            data.lastName,
            data.eMail,
            data.jobTitle,
            data.department,
            data.companyName,
            data.street,
            data.city,
            data.state,
            data.postalCode,
            data.country,
            data.workPhoneNumbers,
            data.mobilePhoneNumbers,
            data.otherPhoneNumbers,
            data.custom1,
            data.custom2,
            data.tags,
            data.aliases);
    }

    constructor(
        directoryType?: string,
        companyId?: string,
        userId?: string,
        id?: string,
        firstName?: string,
        lastName?: string,
        eMail?: string,
        jobTitle?: string,
        department?: string,
        companyName?: string,
        street?: string,
        city?: string,
        state?: string,
        postalCode?: string,
        country?: string,
        workPhoneNumbers?: string[],
        mobilePhoneNumbers?: string[],
        otherPhoneNumbers?: string[],
        custom1?: string,
        custom2?: string,
        tags?: string[],
        aliases?: { type: string; code: string }[]
    ) {
        if (directoryType) { this.directoryType = directoryType; }
        if (companyId) { this.companyId = companyId; }
        if (userId) { this.userId = userId; }
        if (id) { this.id = id; }
        if (firstName) { this.firstName = firstName; }
        if (lastName) { this.lastName = lastName; }
        if (eMail) { this.eMail = eMail; }
        if (jobTitle) { this.jobTitle = jobTitle; }
        if (department) { this.department = department; }
        if (companyName) { this.companyName = companyName; }
        if (street) { this.street = street; }
        if (city) { this.city = city; }
        if (state) { this.state = state; }
        if (postalCode) { this.postalCode = postalCode; }
        if (country) { this.country = country; }
        if (workPhoneNumbers) { this.workPhoneNumbers = workPhoneNumbers; }
        if (mobilePhoneNumbers) { this.mobilePhoneNumbers = mobilePhoneNumbers; }
        if (otherPhoneNumbers) { this.otherPhoneNumbers = otherPhoneNumbers; }
        if (custom1) { this.custom1 = custom1; }
        if (custom2) { this.custom2 = custom2; }
        if (tags) { this.tags = tags; }
        if (aliases) { this.aliases = aliases; }
    }

    public toJSON() {
        if ((this.companyId) && (this.companyId !== "")) {
            return {
                companyId: this.companyId,
                id: this.id,
                firstName: this.firstName,
                lastName: this.lastName,
                eMail: this.eMail,
                jobTitle: this.jobTitle,
                department: this.department,
                companyName: this.companyName,
                street: this.street,
                city: this.city,
                state: this.state,
                postalCode: this.postalCode,
                country: this.country,
                workPhoneNumbers: this.workPhoneNumbers,
                mobilePhoneNumbers: this.mobilePhoneNumbers,
                otherPhoneNumbers: this.otherPhoneNumbers,
                custom1: this.custom1,
                custom2: this.custom2,
                tags: this.tags,
                aliases: this.aliases
            };
        }

        // Return a JSON object for PERSONAL directory contact
        return {
            id: this.id,
            firstName: this.firstName,
            lastName: this.lastName,
            eMail: this.eMail,
            jobTitle: this.jobTitle,
            department: this.department,
            companyName: this.companyName,
            street: this.street,
            city: this.city,
            state: this.state,
            postalCode: this.postalCode,
            country: this.country,
            workPhoneNumbers: this.workPhoneNumbers,
            mobilePhoneNumbers: this.mobilePhoneNumbers,
            otherPhoneNumbers: this.otherPhoneNumbers,
            custom1: this.custom1,
            custom2: this.custom2,
            tags: this.tags,
            aliases: this.aliases
        };
    }

}
